/**
 * Device Authentication Context
 *
 * Provides device fingerprinting and registration functionality
 * for the device authentication flow.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {
  generateFingerprint,
  getDeviceInfo,
  getDeviceToken,
  setDeviceToken as saveDeviceToken,
  clearDeviceToken,
} from '../utils/deviceFingerprint';
import { devicesApi, DeviceCheckResult } from '../services/devicesApi';

interface DeviceInfo {
  deviceName: string;
  browser: string;
  os: string;
}

interface DeviceAuthContextType {
  /** Device token from localStorage */
  deviceToken: string | null;
  /** Generated fingerprint */
  fingerprint: string | null;
  /** Device info (name, browser, os) */
  deviceInfo: DeviceInfo | null;
  /** Whether device info is ready */
  isDeviceReady: boolean;
  /** Check device status for a user */
  checkDevice: (userId: string) => Promise<DeviceCheckResult>;
  /** Register a new device */
  registerDevice: (
    userId: string,
    name: string,
    phone: string,
    organizationId: string
  ) => Promise<{ success: boolean; deviceToken?: string; message: string }>;
  /** Set device token after login */
  setDeviceToken: (token: string) => void;
  /** Clear device token on logout */
  clearDevice: () => void;
}

const DeviceAuthContext = createContext<DeviceAuthContextType | undefined>(undefined);

interface DeviceAuthProviderProps {
  children: ReactNode;
}

export function DeviceAuthProvider({ children }: DeviceAuthProviderProps) {
  const [deviceToken, setDeviceTokenState] = useState<string | null>(null);
  const [fingerprint, setFingerprint] = useState<string | null>(null);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [isDeviceReady, setIsDeviceReady] = useState(false);

  // Initialize device info on mount
  useEffect(() => {
    const initDevice = async () => {
      try {
        const fp = await generateFingerprint();
        const info = getDeviceInfo();
        const token = getDeviceToken();

        setFingerprint(fp);
        setDeviceInfo(info);
        setDeviceTokenState(token);
        setIsDeviceReady(true);
      } catch (error) {
        console.error('Failed to initialize device info:', error);
        setIsDeviceReady(true); // Still ready, just without fingerprint
      }
    };

    initDevice();
  }, []);

  const checkDevice = useCallback(
    async (userId: string): Promise<DeviceCheckResult> => {
      if (!fingerprint) {
        return {
          isKnown: false,
          message: 'Device fingerprint not available',
        };
      }

      try {
        const result = await devicesApi.checkDevice(
          userId,
          fingerprint,
          deviceToken || undefined
        );
        return result;
      } catch (error) {
        console.error('Failed to check device:', error);
        return {
          isKnown: false,
          message: 'Failed to check device status',
        };
      }
    },
    [fingerprint, deviceToken]
  );

  const registerDevice = useCallback(
    async (
      userId: string,
      name: string,
      phone: string,
      organizationId: string
    ): Promise<{ success: boolean; deviceToken?: string; message: string }> => {
      if (!fingerprint) {
        return {
          success: false,
          message: 'Device fingerprint not available',
        };
      }

      try {
        const result = await devicesApi.registerDevice({
          userId,
          fingerprint,
          registeredName: name,
          registeredPhone: phone,
          deviceName: deviceInfo?.deviceName,
          browser: deviceInfo?.browser,
          os: deviceInfo?.os,
          organizationId,
        });

        if (result.success && result.deviceToken) {
          saveDeviceToken(result.deviceToken);
          setDeviceTokenState(result.deviceToken);
        }

        return {
          success: result.success,
          deviceToken: result.deviceToken,
          message: result.message,
        };
      } catch (error) {
        console.error('Failed to register device:', error);
        return {
          success: false,
          message: 'Failed to register device',
        };
      }
    },
    [fingerprint, deviceInfo]
  );

  const setDeviceToken = useCallback((token: string) => {
    saveDeviceToken(token);
    setDeviceTokenState(token);
  }, []);

  const clearDevice = useCallback(() => {
    clearDeviceToken();
    setDeviceTokenState(null);
  }, []);

  return (
    <DeviceAuthContext.Provider
      value={{
        deviceToken,
        fingerprint,
        deviceInfo,
        isDeviceReady,
        checkDevice,
        registerDevice,
        setDeviceToken,
        clearDevice,
      }}
    >
      {children}
    </DeviceAuthContext.Provider>
  );
}

export function useDeviceAuth() {
  const context = useContext(DeviceAuthContext);
  if (context === undefined) {
    throw new Error('useDeviceAuth must be used within a DeviceAuthProvider');
  }
  return context;
}
